import { useMediaQuery, useTheme } from '@material-ui/core'
import { EuOrganization, RequestContactSettingFieldValue, SiteShare } from '@noco/http-client/lib/noco'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ModalAuthentication } from 'src/components/modals/ModalAuthentication'
import { ModalVisitorConnectToContact } from 'src/components/modals/ModalVisitorConnectToContact'
import { SiteDetail } from 'src/components/organisms/SiteDetail'
import {
  useSendAuthenticationCode,
  useUnifiedFormSignin,
  useVerifyAuthenticationCode
} from 'src/fixtures/modules/contactAuth/hooks'
import { useGetContactSiteShare, useGetContactSiteUnifiedShareForm } from 'src/fixtures/modules/contactSite/hooks'
import { extractEmailFromRequestValues } from 'src/fixtures/modules/contact/utils'
import { useGetVisitor } from 'src/fixtures/modules/visitor/hooks'

export interface PageSiteShareWithLinkProps {
  siteId: string
  shareId: string
  siteShare: SiteShare
  hasMaterialContactable?: boolean
  organization: EuOrganization
  contactExpiredOn?: string | null
}

export const PageSiteShareWithLink = ({
  siteId,
  siteShare,
  shareId,
  organization,
  contactExpiredOn
}: PageSiteShareWithLinkProps) => {
  const theme = useTheme()
  const isPCView = useMediaQuery(theme.breakpoints.up('sm'))
  const { data, mutate: mutateSiteShare } = useGetContactSiteShare(shareId)
  const { mutate: mutateVisitor, data: visitor } = useGetVisitor()
  const { handleVerifyAuthCode } = useVerifyAuthenticationCode()
  const { handleUnifiedFormSignin } = useUnifiedFormSignin()
  const { handleSendAuthCode } = useSendAuthenticationCode()
  const resendingRef = useRef(false)
  const lastResendAtRef = useRef(0)
  const [openVerifyCodeModal, setOpenVerifyCodeModal] = useState(false)
  const [lastSubmittedValues, setLastSubmittedValues] = useState<RequestContactSettingFieldValue[] | undefined>()
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState('')
  const [isInit, setIsInit] = useState(false)

  const site = data?.site
  const { data: unifiedShareFormsRes, mutate: mutateUnifiedShareForms } = useGetContactSiteUnifiedShareForm(shareId)

  const isFormActive = siteShare?.isFormActive
  const shareForms = unifiedShareFormsRes?.shareForms
  const noForms = useMemo(() => !!shareForms && shareForms.length === 0, [shareForms])
  const openUnifiedForm = useMemo(
    () => !!isFormActive && !noForms && !openVerifyCodeModal && !!shareForms,
    [isFormActive, noForms, openVerifyCodeModal, shareForms]
  )
  const isActivated = useMemo(() => !isFormActive || noForms, [isFormActive, noForms])

  const syncDocument = useCallback(() => {
    setOpenVerifyCodeModal(false)
    mutateSiteShare()
    setTimeout(() => {
      mutateVisitor().then(() => mutateUnifiedShareForms())
    }, 128)
  }, [mutateSiteShare, mutateUnifiedShareForms, mutateVisitor])

  const handleSubmitUnifiedForm = useCallback(
    async (values: RequestContactSettingFieldValue[] | undefined) => {
      if (!values) return
      const email = extractEmailFromRequestValues(values, shareForms) ?? ''
      const result = await handleUnifiedFormSignin({
        siteId,
        contactSettingFieldValues: values
      })
      setLastSubmittedValues(values)
      if (email) setLastSubmittedEmail(email)
      if (result?.status === 'require_auth_code') {
        setOpenVerifyCodeModal(true)
      } else if (result?.status === 'skip_auth') {
        syncDocument()
      }
    },
    [handleUnifiedFormSignin, shareForms, siteId, syncDocument]
  )

  const handleResendAuthCode = useCallback(async () => {
    if (!lastSubmittedValues || resendingRef.current) return
    if (Date.now() - lastResendAtRef.current < 5000) return
    resendingRef.current = true
    lastResendAtRef.current = Date.now()
    try {
      await handleSendAuthCode({
        siteId,
        contactSettingFieldValues: lastSubmittedValues
      })
    } finally {
      resendingRef.current = false
    }
  }, [handleSendAuthCode, lastSubmittedValues, siteId])

  const onSubmitModalAuthentication = useCallback(
    (code: string) => {
      handleVerifyAuthCode({ code })
        .then(() => syncDocument())
        .catch(() => {
          // 失敗時は認証コードモーダルに留まる
        })
    },
    [handleVerifyAuthCode, syncDocument]
  )

  useEffect(() => {
    if (visitor && site) {
      setIsInit(true)
    }
  }, [visitor, site])

  return (
    <>
      {site && (
        <SiteDetail
          isActivated={!!isActivated}
          siteSections={site.siteSections || []}
          site={site}
          isPC={isPCView}
          mode="eu"
          contactExpiredOn={contactExpiredOn}
          organization={organization}
        />
      )}

      {/* 統合フォームモーダル */}
      {isInit && (
        <ModalVisitorConnectToContact
          organizationName={organization?.name}
          siteId={siteId}
          open={openUnifiedForm}
          onClose={() => {}}
          policySettings={organization.policySettings}
          onSubmit={handleSubmitUnifiedForm}
          siteShareForms={shareForms}
          materialTitle={site?.title ?? undefined}
        />
      )}

      {/* 認証コード入力モーダル */}
      <ModalAuthentication
        open={openVerifyCodeModal}
        email={lastSubmittedEmail}
        resend={handleResendAuthCode}
        onSubmit={onSubmitModalAuthentication}
        onClose={() => {}}
      />
    </>
  )
}
